import { imageUrlRoot } from 'app/constants'
import SimpleLink from 'core/components/SimpleLink'
import Grid from 'core/elements/grid'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import { routeManager } from 'core/utils/routes'
import React, { useCallback } from 'react'
import useReactRouter from 'use-react-router'
import { formatMonthlyCost } from './helpers'
import Text from 'core/elements/Text'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import clsx from 'clsx'

const platformLogoMap = {
  openstack: `${imageUrlRoot}/opencloud/openstack-logo.png`,
  vmware: `${imageUrlRoot}/opencloud/vmware-logo.png`,
}

const PlatformCell = ({ value }) => {
  const logo = platformLogoMap[value]
  const height = value === 'openstack' ? 17 : 40
  return <img src={logo || null} alt={`${value} logo`} style={{ height }} />
}

const MonthlyCostCell = ({ value }) => {
  return (
    <Text variant="body2">
      <b>{formatMonthlyCost(value)}</b> /mo
    </Text>
  )
}

const AlertCountCell = ({ value: alerts }) => {
  const numAlerts = alerts?.length || 0
  const classes = useAlertCountCellStyles()
  return (
    <div
      className={clsx(
        classes.alertCountContainer,
        numAlerts > 0 && classes.alertCountContainerWithAlerts,
      )}
    >
      <Text
        variant="caption1"
        className={clsx(classes.alertCount, numAlerts > 0 && classes.alertCountWithAlerts)}
      >
        {numAlerts}
      </Text>
    </div>
  )
}

const useAlertCountCellStyles = makeStyles<Theme>((theme) => ({
  alertCountContainer: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: '4px',
    padding: theme.spacing(1),
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  alertCountContainerWithAlerts: {
    backgroundColor: theme.palette.red[700],
  },
  alertCount: {
    color: theme.palette.common.black,
  },
  alertCountWithAlerts: {
    color: theme.palette.common.white,
  },
}))

interface RegionListTableProps {
  data: any[]
  onRegionSelect: (regionId: string) => void
}

export default function RegionsListTable({ data, onRegionSelect }: RegionListTableProps) {
  const { history } = useReactRouter()

  const NameLinkCell = useCallback(
    ({ value }) => {
      return <SimpleLink onClick={() => onRegionSelect(value)}>{<b>{value}</b>}</SimpleLink>
    },
    [onRegionSelect],
  )

  const rowMenuItems: Array<GridRowMenuItemSpec<any>> = [
    {
      label: 'View Alerts',
      icon: 'exclamation-circle',
      handleClick: (region) => {
        history.push(routeManager.routes.opencloud.alerts.path({ region }))
      },
    },
    {
      label: 'View Admin Console',
      icon: 'user',
      handleClick: (region) => {
        history.push(routeManager.routes.opencloud.adminConsole.path({ region: region.name }))
      },
    },
  ]

  const columns: GridViewColumn<any>[] = [
    { key: 'name', label: 'Name', CellComponent: NameLinkCell },
    { key: 'type', label: 'Platform', CellComponent: PlatformCell },
    { key: 'region', label: 'Region' },
    { key: 'infra.vcpus', label: 'vCPUs' },
    {
      key: 'infra.ram',
      label: 'Memory (GiB)',
      formatFn: (value) => `${Math.round((value / 1024) * 100) / 100}`,
    },
    {
      key: 'infra.disk',
      label: 'Storage (GiB)',
      formatFn: (value) => `${Math.round((value / 1024) * 100) / 100}`,
    },
    {
      key: 'infra',
      label: 'Network',
      formatFn: (infra) =>
        infra?.network ? `${infra?.network?.vendor} ${infra?.network?.product}` : '',
    },
    { key: 'monthlyCost', label: 'Monthly Cost', CellComponent: MonthlyCostCell },
    { key: 'alerts', label: 'Alerts', CellComponent: AlertCountCell },
  ]

  return (
    <Grid
      uniqueIdentifier="id"
      columns={columns}
      data={data}
      orderDirection="asc"
      orderBy="id"
      rowMenuItems={rowMenuItems}
      disableToolbar
      disablePagination
    />
  )
}
